type Listener = () => void;

const PROBE_TIMEOUT_MS = 5000;
const RETRY_INTERVAL_MS = 10000;

const listeners = new Set<Listener>();
let offline = typeof navigator !== 'undefined' ? !navigator.onLine : false;
let probeInFlight: Promise<boolean> | null = null;
let retryTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Notifies every subscriber that the connectivity flag may have changed.
 */
function emit(): void {
  listeners.forEach((listener) => listener());
}

/**
 * Updates the offline flag and notifies subscribers on change.
 * @param value - True when the app has no usable connection.
 */
function setOffline(value: boolean): void {
  if (offline === value) {
    return;
  }
  offline = value;
  emit();
}

/**
 * Checks real reachability with a tiny uncached request to the connectivity probe URL.
 * Concurrent callers share one in-flight request.
 * @returns True when the origin answered, false on network failure or timeout.
 */
function probe(): Promise<boolean> {
  if (probeInFlight) {
    return probeInFlight;
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  probeInFlight = Promise.resolve()
    .then(() =>
      fetch(getProbeUrl(), {
        method: 'HEAD',
        cache: 'no-store',
        mode: 'no-cors',
        signal: controller.signal,
      }),
    )
    .then(() => true)
    .catch(() => false)
    .finally(() => {
      clearTimeout(timer);
      probeInFlight = null;
    });
  return probeInFlight;
}

/**
 * Probes reachability and applies the result to the offline flag.
 * @returns Resolves once the flag has been updated.
 */
async function probeAndUpdate(): Promise<void> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    setOffline(true);
    return;
  }
  const reachable = await probe();
  setOffline(!reachable);
}

/**
 * Subscribes to connectivity changes. Signature matches React's useSyncExternalStore.
 * @param listener - Called whenever the offline flag changes.
 * @returns Unsubscribe function.
 */
export function subscribeConnectivity(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Reads the current offline flag.
 * @returns True when the app currently has no usable connection.
 */
export function isAppOffline(): boolean {
  return offline;
}

/**
 * Called when an API request failed without any server response; confirms with a probe before
 * showing the offline bar, so a single server-side failure is not mistaken for lost internet.
 */
export function reportNetworkFailure(): void {
  probeAndUpdate();
}

/**
 * Called when an API request got a response; clears the offline state immediately.
 */
export function reportNetworkSuccess(): void {
  if (offline) {
    setOffline(false);
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => setOffline(true));
  window.addEventListener('online', () => {
    probeAndUpdate();
  });
  subscribeConnectivity(syncRetryTimer);
  syncRetryTimer();
}

/**
 * Builds the URL used to verify real connectivity. Prefers the API server's /api/health
 * endpoint, since that is what the app needs to function and the endpoint is unauthenticated,
 * tenant-free and registered ahead of the rate limiter and routes, so a probe costs the server
 * almost nothing. The bar therefore also shows when only the wider internet is down while a
 * local network stays up. Falls back to a static asset on the app's own origin when the API
 * env vars are not configured. The no-cors probe counts any answer as reachable.
 * @returns An uncacheable probe URL.
 */
function getProbeUrl(): string {
  const protocol = process.env.REACT_APP_PROTOCOL;
  const apiHost = process.env.REACT_APP_API_URL;
  if (protocol && apiHost) {
    const slugName = window.location.hostname?.split('.')?.[0];
    return `${protocol}${slugName}${apiHost}/api/health?_=${Date.now()}`;
  }
  return `${window.location.origin}/favicon.ico?_=${Date.now()}`;
}

/**
 * Probes connectivity immediately. Used by the offline bar's Retry action.
 * @returns Resolves once the offline flag reflects the probe result.
 */
export function retryNow(): Promise<void> {
  return probeAndUpdate();
}

/**
 * Keeps the background re-check timer in step with the offline flag: it exists only while the
 * app is offline and is cleared the moment the connection returns, so nothing is scheduled
 * during normal use.
 */
function syncRetryTimer(): void {
  if (offline && !retryTimer) {
    retryTimer = setInterval(() => {
      probeAndUpdate();
    }, RETRY_INTERVAL_MS);
    return;
  }
  if (!offline && retryTimer) {
    clearInterval(retryTimer);
    retryTimer = null;
  }
}
